import React from 'react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES, SPACING_AMOUNT_OPTIONS } from '../common/commonDefaults';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getDividerExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Divider Spacing</h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Spacing</label>
        <div className="flex gap-1.5">
          {SPACING_AMOUNT_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange({ spacing: opt.value })}
              className={`flex-1 py-1.5 rounded text-sm font-medium transition-colors ${content.spacing === opt.value ? 'bg-[#749555] text-white' : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'}`}>{opt.label}</button>
          ))}
        </div>
      </div>
    </div>
  ),
  colorsFontsExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Divider Color</h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Color</label>
        <div className="flex items-center gap-2">
          <input type="color" value={content.color || '#749555'} onChange={(e) => onChange({ color: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-700 flex-shrink-0" />
          <input type="text" value={content.color || '#749555'} onChange={(e) => onChange({ color: e.target.value })}
            className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none" />
        </div>
      </div>
    </div>
  ),
});

/**
 * DividerEditor - Editor component for divider block
 */
const DividerEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const extras = getDividerExtraStyleControls({ content, onChange });

  return (
    <div className="space-y-4">
      {/* Divider Style */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Style
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'line', label: 'Solid' },
            { value: 'dotted', label: 'Dotted' },
            { value: 'gradient', label: 'Gradient' },
            { value: 'space', label: 'Spacer' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('dividerStyle', option.value)}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.dividerStyle === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Width (only show if not spacer) */}
      {content.dividerStyle !== 'space' && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Width
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'narrow', label: 'Narrow' },
              { value: 'medium', label: 'Medium' },
              { value: 'wide', label: 'Wide' },
              { value: 'full', label: 'Full' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('width', option.value)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  content.width === option.value
                    ? 'bg-[#749555] text-white'
                    : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Preview
        </label>
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 flex items-center justify-center">
          <DividerPreview content={content} />
        </div>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['divider']} extraControls={extras.sizeSpacingExtras} />
          <ColorsFontsPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['divider']} theme={theme || {}} blockType="divider" extraControls={extras.colorsFontsExtras} />
        </>
      )}
    </div>
  );
};

// Small preview component for the editor
const DividerPreview = ({ content }) => {
  const { dividerStyle = 'line', width = 'medium', color = 'default' } = content;

  const widthClasses = {
    narrow: 'w-1/4',
    medium: 'w-1/2',
    wide: 'w-3/4',
    full: 'w-full',
  };

  const colorClasses = {
    default: 'border-gray-600',
    accent: 'border-[#749555]',
    subtle: 'border-gray-800',
  };

  if (dividerStyle === 'space') {
    return <div className="text-gray-500 text-xs">Spacer (no visible line)</div>;
  }

  if (dividerStyle === 'gradient') {
    const gradientColors = {
      default: 'from-transparent via-gray-600 to-transparent',
      accent: 'from-transparent via-[#749555] to-transparent',
      subtle: 'from-transparent via-gray-700 to-transparent',
    };
    return (
      <div className={`${widthClasses[width]} h-px bg-gradient-to-r ${gradientColors[color]}`} />
    );
  }

  return (
    <div
      className={`${widthClasses[width]} border-t ${colorClasses[color] || ''} ${
        dividerStyle === 'dotted' ? 'border-dotted' : 'border-solid'
      }`}
    />
  );
};

export default DividerEditor;
